import { type FC, Suspense, useState } from "react";
import { Form } from "react-bootstrap";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import type { MetaAdsCampaignTemplate } from "shared";
import { Icon } from "../common/Icon";
import { useModal } from "../common/modal/Modal";
import { Card } from "../components/Card";
import { Select } from "../components/Select";
import { LinkGen } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import { ModalCampaignTemplate } from "./templates/ModalCampaignTemplate";
import {
	useCampaignTemplateActions,
	useCampaignTemplates,
} from "./useCampaignTemplates";
import { useCanEditMetaAds } from "./useMyMetaAdsRole";

const OBJECTIVE_LABELS: Record<string, string> = {
	OUTCOME_AWARENESS: "Awareness",
	OUTCOME_TRAFFIC: "Traffic",
	OUTCOME_ENGAGEMENT: "Engagement",
	OUTCOME_LEADS: "Leads",
	OUTCOME_SALES: "Sales",
};

const TemplatesTable: FC = () => {
	const { data: templates } = useCampaignTemplates();
	const { createTemplate, updateTemplate, deleteTemplate } =
		useCampaignTemplateActions();
	const { open } = useModal();
	const navigate = useNavigate();
	const [objectiveFilter, setObjectiveFilter] = useState<string>("");
	const canEdit = useCanEditMetaAds();

	const filtered = objectiveFilter
		? templates.filter((t) => t.objective === objectiveFilter)
		: templates;

	const handleCreate = () => {
		open(ModalCampaignTemplate, {
			onSave: async (dto) => {
				await createTemplate.mutateAsync(dto);
			},
		});
	};

	const handleEdit = (template: MetaAdsCampaignTemplate) => {
		open(ModalCampaignTemplate, {
			initial: template,
			onSave: async (dto) => {
				await updateTemplate.mutateAsync({
					id: template.id,
					dto: {
						name: dto.name,
						description: dto.description,
						settings: dto.settings,
					},
				});
			},
		});
	};

	const handleCreateCampaign = (template: MetaAdsCampaignTemplate) => {
		navigate(LinkGen.MetaAds.CampaignCreate(), {
			state: { fromTemplate: template },
		});
	};

	return (
		<>
			<div className="d-flex gap-3 align-items-center mb-3">
				<Form.Group style={{ minWidth: 200 }}>
					<Select
						value={objectiveFilter}
						onChange={(e) => setObjectiveFilter(e.target.value)}
					>
						<option value="">All Objectives</option>
						{Object.entries(OBJECTIVE_LABELS).map(([value, label]) => (
							<option key={value} value={value}>
								{label}
							</option>
						))}
					</Select>
				</Form.Group>
				{canEdit && (
					<button
						type="button"
						className="btn btn-primary ms-auto"
						onClick={handleCreate}
					>
						<Icon name="add" size={18} /> Create Template
					</button>
				)}
			</div>

			<Card>
				<Card.Body style={{ padding: 0 }}>
					{filtered.length === 0 ? (
						<p className="text-muted" style={{ padding: 16, marginBottom: 0 }}>
							{objectiveFilter
								? "No templates found for this objective."
								: "No campaign templates yet. Create your first one."}
						</p>
					) : (
						<table className="table" style={{ marginBottom: 0 }}>
							<thead>
								<tr>
									<th>Name</th>
									<th>Description</th>
									<th>Objective</th>
									<th>Created</th>
									{canEdit && <th>Actions</th>}
								</tr>
							</thead>
							<tbody>
								{filtered.map((t) => (
									<tr key={t.id}>
										<td>{t.name}</td>
										<td>
											<span
												className="text-muted"
												style={{
													maxWidth: 200,
													display: "inline-block",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{t.description || "-"}
											</span>
										</td>
										<td>
											<span className="badge bg-primary">
												{OBJECTIVE_LABELS[t.objective] ?? t.objective}
											</span>
										</td>
										<td>{new Date(t.createdAt).toLocaleDateString()}</td>
										{canEdit && (
											<td>
												<div className="d-flex gap-1">
													<button
														type="button"
														className="btn btn-sm btn-outline-primary"
														onClick={() => handleEdit(t)}
													>
														Edit
													</button>
													<button
														type="button"
														className="btn btn-sm btn-outline-danger"
														onClick={() => deleteTemplate.mutate(t.id)}
														disabled={deleteTemplate.isPending}
													>
														Delete
													</button>
													<button
														type="button"
														className="btn btn-sm btn-primary"
														onClick={() => handleCreateCampaign(t)}
													>
														Create Campaign
													</button>
												</div>
											</td>
										)}
									</tr>
								))}
							</tbody>
						</table>
					)}
				</Card.Body>
			</Card>
		</>
	);
};

export const PageCampaignTemplates: FC = () => {
	return (
		<AppPage title="Campaign Templates">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load campaign templates.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading templates...</p>}>
						<TemplatesTable />
					</Suspense>
				</ErrorBoundary>
			</div>
		</AppPage>
	);
};
